import React, { forwardRef, ButtonHTMLAttributes } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { clsx } from 'clsx';

// Omit conflicting props from ButtonHTMLAttributes
type SafeButtonHTMLAttributes = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onAnimationStart' | 'onDragStart' | 'onDragEnd' | 'onDrag'
>;

export interface ButtonProps extends SafeButtonHTMLAttributes {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  gradient?: boolean;
  as?: 'button' | 'span' | 'div'; // Support polymorphic rendering
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      iconPosition = 'left',
      fullWidth = false,
      gradient = false,
      className,
      disabled,
      as = 'button',
      ...props
    },
    ref
  ) => {
    const baseStyles = clsx(
      'inline-flex items-center justify-center gap-2',
      'font-medium rounded-lg transition-all duration-300',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      {
        'w-full': fullWidth,
      }
    );

    const sizeStyles = {
      sm: 'h-8 px-3 text-sm',
      md: 'h-10 px-4 text-base',
      lg: 'h-12 px-6 text-lg',
    };

    const variantStyles = {
      primary: gradient
        ? 'bg-gradient-primary text-white hover:shadow-lg focus-visible:ring-primary-500'
        : 'bg-primary-500 text-white hover:bg-primary-600 focus-visible:ring-primary-500',
      secondary: gradient
        ? 'bg-gradient-secondary text-white hover:shadow-lg focus-visible:ring-secondary-500'
        : 'bg-secondary-500 text-white hover:bg-secondary-600 focus-visible:ring-secondary-500',
      outline:
        'border-2 border-primary-500 text-primary-600 hover:bg-primary-50 focus-visible:ring-primary-500',
      ghost: 'text-primary-600 hover:bg-primary-50 focus-visible:ring-primary-500',
      danger:
        'bg-error-500 text-white hover:bg-error-600 focus-visible:ring-error-500',
    };

    const combinedStyles = clsx(
      baseStyles,
      sizeStyles[size],
      variantStyles[variant],
      className
    );

    const motionProps = {
      className: combinedStyles,
      whileTap: { scale: disabled || loading ? 1 : 0.98 },
      whileHover: { scale: disabled || loading ? 1 : 1.02 },
      transition: { duration: 0.15 },
    };

    const content = (
      <>
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {!loading && icon && iconPosition === 'left' && icon}
        {children}
        {!loading && icon && iconPosition === 'right' && icon}
      </>
    );

    if (as === 'span') {
      return (
        <motion.span
          className={combinedStyles}
          {...motionProps}
        >
          {content}
        </motion.span>
      );
    }

    if (as === 'div') {
      return (
        <motion.div
          className={combinedStyles}
          {...motionProps}
        >
          {content}
        </motion.div>
      );
    }

    return (
      <motion.button
        ref={ref}
        disabled={disabled || loading}
        {...motionProps}
        {...(props as any)}
      >
        {content}
      </motion.button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
